import React, { useState } from 'react';
import { UsersIcon, ActivityIcon, HeartIcon, MedalIcon, TargetIcon, ZapIcon } from 'lucide-react';
import { Button } from '../components/Button';
import { SectionHeader } from '../components/SectionHeader';
import { FeatureCard } from '../components/FeatureCard';
import { ActivityCard } from '../components/ActivityCard';
import { ValuesSection } from '../components/ValuesSection';
import { CoachCard, Coach } from '../components/CoachCard';
import { CoachModal } from '../components/CoachModal';
import { coaches } from '../data/coaches';
export function HomePage() {
  const [selectedCoach, setSelectedCoach] = useState<Coach | null>(null);
  const features = [{
    icon: <ActivityIcon className="w-6 h-6" data-id="element-886" />,
    title: 'Multi-Sport Development',
    description: 'Exposure to multiple sports builds well-rounded athletes with diverse skills.'
  }, {
    icon: <UsersIcon className="w-6 h-6" data-id="element-887" />,
    title: 'Confidence & Friendships',
    description: 'Our sessions foster self-belief, teamwork, and lasting friendships.'
  }, {
    icon: <ZapIcon className="w-6 h-6" data-id="element-888" />,
    title: 'Motor Skills',
    description: 'Age-appropriate activities develop coordination, agility, and physical literacy.'
  }, {
    icon: <MedalIcon className="w-6 h-6" data-id="element-889" />,
    title: 'Expert-Led Programmes',
    description: 'Every session designed and delivered by qualified Olympian coaches.'
  }, {
    icon: <TargetIcon className="w-6 h-6" data-id="element-890" />,
    title: 'Coaching for Every Age',
    description: 'Tailored programmes from early years through to competitive juniors.'
  }, {
    icon: <HeartIcon className="w-6 h-6" data-id="element-891" />,
    title: 'Healthy Body & Mind',
    description: 'Building lifelong habits for physical and mental wellbeing.'
  }];
  const activities = [{
    title: 'Badminton',
    description: 'From first racket to competitive play, our Olympian-led badminton programme develops technique, agility, and game sense.',
    slug: 'badminton',
    colorAccent: 'blue' as const,
    icon: <ActivityIcon className="w-32 h-32" data-id="element-892" />,
    image: 'https://cdn.magicpatterns.com/uploads/h1HjTQtx6uRzRJncSSv8d2/badminton.webp'
  }, {
    title: 'Multi-Sports',
    description: 'A dynamic mix of sports and activities designed to build all-round athletic ability and a love of movement.',
    slug: 'multi-sports',
    colorAccent: 'coral' as const,
    icon: <TargetIcon className="w-32 h-32" data-id="element-893" />,
    image: 'https://cdn.magicpatterns.com/uploads/nius5mV4GSJtkuCgu886pQ/Multisports.webp'
  }, {
    title: 'Netball',
    description: 'Build court skills, tactical awareness, and team spirit in our inclusive netball sessions for all abilities.',
    slug: 'netball',
    colorAccent: 'green' as const,
    icon: <UsersIcon className="w-32 h-32" data-id="element-894" />,
    image: 'https://cdn.magicpatterns.com/uploads/4gCkFMntbwDcZHYhPNEDey/Netball.jpg'
  }];
  return <div className="w-full overflow-hidden pt-20" data-id="element-895">
      {/* SECTION 1: HERO */}
      <section className="relative pt-28 pb-20 md:pt-36 md:pb-28 min-h-[320px] md:min-h-[400px] bg-brand-blue overflow-hidden" data-id="element-896">
        <img src="https://cdn.magicpatterns.com/uploads/pTRbE2fvqTGzPDd7dvdT5h/Group1.webp" alt="" className="absolute inset-0 w-full h-full object-cover" aria-hidden="true" data-id="element-897" />
        <div className="absolute inset-0 bg-brand-blue/85" data-id="element-898"></div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10" data-id="element-899">
          <div className="max-w-3xl" data-id="element-900">
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-display font-bold text-white mb-6 leading-tight" data-id="element-901">
              <span className="text-[#00A650]" data-id="element-902">Inspire.</span> Encourage.{' '}
              <span className="text-brand-yellow" data-id="element-903">Achieve.</span>
            </h1>

            <p className="text-lg md:text-xl text-white/90 mb-8 max-w-xl leading-relaxed" data-id="element-904">
              Expert sport coaching led by our Olympian coaches Donna Kellogg
              MBE, Natalie Barton and Sarah Chapman, building confidence,
              skills, and friendships for young athletes across the UK.
            </p>

            <div className="flex flex-col sm:flex-row gap-3 mb-10" data-id="element-905">
              <Button href="/book" variant="primary" size="lg" icon data-id="element-906">
                Book a Session
              </Button>
              <Button onClick={() => document.getElementById('mission')?.scrollIntoView({
              behavior: 'smooth'
            })} variant="secondary" size="lg" className="border-white text-white hover:bg-white/20" data-id="element-907">
                Learn More
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* SECTION 2: MISSION / BENEFITS */}
      <section id="mission" className="py-24 bg-navy-50" data-id="element-908">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-909">
          <SectionHeader eyebrow="Our Mission" title="From Grassroots to Elite" description="We believe every child deserves access to world-class coaching. Our programmes are designed to develop not just better athletes, but confident, resilient young people." data-id="element-910" />

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8" data-id="element-911">
            {features.map((feature, index) => <FeatureCard key={index} icon={feature.icon} title={feature.title} description={feature.description} index={index} data-id="element-912" />)}
          </div>
        </div>
      </section>

      {/* SECTION 3: ACTIVITIES */}
      <section className="py-24 bg-white" data-id="element-913">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-914">
          <SectionHeader eyebrow="What We Offer" title="Our Programmes" description="Discover our range of expertly designed coaching sessions, tailored for different age groups and skill levels." data-id="element-915" />

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8" data-id="element-916">
            {activities.map((activity, index) => <ActivityCard key={index} {...activity} index={index} data-id="element-917" />)}
          </div>
        </div>
      </section>

      {/* SECTION 4: OLYMPIC VALUES */}
      <ValuesSection ctaLabel="Discover Our Medals Programme" data-id="element-918" />

      {/* SECTION 5: MEET THE TEAM */}
      <section className="py-24 bg-navy-50" data-id="element-919">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-920">
          <SectionHeader eyebrow="Our Coaches" title="Meet the Team" description="Learn from the best. Our lead coaches Donna Kellogg MBE, Natalie Barton and Sarah Chapman are all Team GB Olympians dedicated to inspiring the next generation through sport." data-id="element-921" />

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-922">
            {coaches.map((coach, index) => <CoachCard key={coach.id} coach={coach} index={index} onViewProfile={setSelectedCoach} data-id="element-923" />)}
          </div>
        </div>
      </section>

      {/* Coach Modal Portal */}
      <CoachModal coach={selectedCoach} isOpen={!!selectedCoach} onClose={() => setSelectedCoach(null)} data-id="element-924" />
    </div>;
}